// @ts-nocheck
import React, { useState } from 'react';
import { EditBase, TopToolbar, SimpleForm, TextInput, SelectInput, SelectArrayInput, ReferenceManyField, Datagrid, TextField, FunctionField, EditButton, DeleteButton, useRecordContext, required } from 'react-admin';
import { Card, CardContent, Typography, Grid, Button, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import { roleChoices, subscriptionStatusChoices } from '../../Services/users';
import { countryChoices } from '../../Services/countries';
import ProjectUserModalCreate from './UserProjects/ProjectUserModalCreate';
import { ActivityCard } from '../Activities/ActivityCard';
import { UserPartnerCard } from './UserPartnerCard';


// L'id de l'utilisateur vient du contexte react-admin : le composant doit donc être
// rendu SOUS EditBase, pas au-dessus (où le record n'existe pas encore).
//
// Filtre par ACTEUR : « ce que cette personne a fait », ce qui traverse les projets et
// les comptes de facturation — d'où les deux colonnes de contexte, gardées. Pas de lien
// sur l'acteur en revanche : il pointerait sur cette page même.
const UserActivitySection = () => {
  const record = useRecordContext();
  if (!record?.id) return null;
  return <ActivityCard filters={{ actorUserId: Number(record.id) }} linkActor={false} />;
};


// Statut d'un rattachement à un compte de facturation. 200 = l'invitation a été
// acceptée ; 0 = elle attend ; 100 = elle a été refusée.
//
// ⚠️ Une invitation en attente ou refusée EST un rattachement : la ligne existe,
// donc l'utilisateur n'est pas « orphelin » au sens du filtre de la liste. C'est
// exactement ce que cette carte permet de voir.
const membershipStatus = (status: number) => {
  if (status === 200) return <Chip size="small" color="success" label="Accepté" />;
  if (status === 100) return <Chip size="small" color="error" label="Refusé" />;
  return <Chip size="small" label="En attente" />;
};


const Breadcrumb = () => {
  const record = useRecordContext();
  return (
    <TopToolbar>
      <Typography variant="subtitle1">
        Home / Users / {record ? record.name : 'Edit'}
      </Typography>
    </TopToolbar>
  );
};


export const UserEdit = (props: any) => {
  
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  return (
  <EditBase mutationMode="pessimistic">
    <>
    <Breadcrumb />
    {/* Utilisation d'une grille à deux colonnes */}
    <Grid container spacing={2}>
      {/* Colonne principale avec les projets associés */}
      <Grid item xs={8}>
        <Card>
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <Typography variant="h6" gutterBottom>
                Associated Projects
              </Typography>
              <Button variant="contained" color="primary" onClick={handleOpen}>
                    +
              </Button>
            </div>

            <ReferenceManyField
              label="Projects"
              reference="project-users"
              target="userId"
              sort={{ field: 'project.name', order: 'ASC' }}
            >
              <Datagrid>
                {/* La fiche projet admin s'indexe sur la clé primaire du projet
                    (`/projects/:id`, cf. ProjectEdit), pas sur le folderId. */}
                <FunctionField
                  label="Project"
                  source="project.name"
                  render={(r) =>
                    r.project?.id ? (
                      <Link to={`/projects/${r.project.id}`}>{r.project.name}</Link>
                    ) : (
                      r.project?.name || '—'
                    )
                  }
                />
                <TextField source="role" label="Role" />
                <EditButton />
                <DeleteButton />
              </Datagrid>
            </ReferenceManyField>
          </CardContent>
        </Card>

        {/* Un utilisateur peut être membre d'un COMPTE sans aucun projet — c'est
            même le cas le plus courant chez un propriétaire qui n'a pas encore
            créé son premier projet. Cette carte manquait : la fiche ne montrait
            que les projets, et un compte actif sans projet y paraissait vide. */}
        <Card style={{ marginTop: 16 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Associated Billing Accounts
            </Typography>
            <ReferenceManyField
              label="Billing accounts"
              reference="billing-account-users"
              target="userId"
            >
              <Datagrid bulkActionButtons={false}>
                <FunctionField
                  label="Billing account"
                  render={(r) =>
                    r.billingAccount?.id ? (
                      <Link to={`/billing-accounts/${r.billingAccount.id}`}>
                        {r.billingAccount.name || '(sans nom)'}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <TextField source="role" label="Role" />
                <FunctionField label="Statut" render={(r) => membershipStatus(r.status)} />
                <TextField source="email" label="Email invité" />
              </Datagrid>
            </ReferenceManyField>
          </CardContent>
        </Card>

        {/* Contexte partenaire : appartenances et demande d'accès. Sans lui, un compte
            venu de l'espace partenaire paraît vide sans qu'on sache pourquoi. */}
        <UserPartnerCard />

        {/* Activité de CET utilisateur (filtre par acteur), sous les projets associés. */}
        <UserActivitySection />
      </Grid>

      {/* Colonne secondaire avec le formulaire utilisateur */}
      <Grid item xs={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Details
            </Typography>
            <SimpleForm>
              <TextInput source="id" disabled />
              <TextInput source="firstname" validate={required()} />
              <TextInput source="lastname" validate={required()} />
              <TextInput source="name" />
              <TextInput source="email" disabled />
              <TextInput source="phone" />
              <TextInput source="companyName" />
              <TextInput source="companyType" />
              <TextInput source="situation" />
              <TextInput source="city" />
              <TextInput source="zipCode" />
              <TextInput source="adress" />
              <SelectInput
                source="country"
                choices={countryChoices}
                optionText="name"
                optionValue="id"
              />
              <SelectArrayInput 
                source="roles" 
                choices={roleChoices} 
                optionText="name" 
                optionValue="id" 
              />
              <SelectInput
                source="subscriptionStatus"
                choices={subscriptionStatusChoices}
                optionText="name"
                optionValue="id"
              />
              <TextInput
                source="brevoContactId"
                label="Brevo Contact ID"
                helperText="ID interne du contact Brevo. Laisser vide pour effacer."
                parse={(v) => (v === '' || v == null ? 0 : Number(v))}
              />
            </SimpleForm>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    <ProjectUserModalCreate open={open} handleClose={handleClose} />
    </>
  </EditBase>
)};
